import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { LinePath } from '@visx/shape';
import { AreaClosed } from '@visx/shape';
import { scaleLinear, scaleTime } from '@visx/scale';
import { extent } from 'd3-array';

import { useTheme } from '@simplywallst/ui-core';

import { getTimeSeriesPeriodMap } from '@/utilities/timeSeriesPeriodMap';
import type { Period } from '@/utilities/timeSeriesPeriodMap';

import XAxis from '@/components/TimeSeries/components/XAxis';

import useWindowSize from '@/hooks/useWindowSize';
import { useDebounce } from '@simplywallst/hooks/useDebounce';
import useIsomorphicLayoutEffect from '@/hooks/useIsomorphicLayoutEffect';

import type { CurrencyISO } from '@/constants/currencies';

import queryFn, { getDefault, Outcome } from './hooks/queryFn';
import { Wrapper, Chart, Status, minHeight } from './styled';
import { DeprecatedNarrativeMarker } from '../Marker';

interface Props {
  companyId: string;
  align?: 'left' | 'right';
  height?: number;
  period?: Period;
  currencyISO?: CurrencyISO;
  includeXAxis?: boolean;
  interactive?: boolean;
  fairValue?: number | null;
}

export const DeprecatedNarrativeMiniPriceChart = ({
  companyId,
  period = '1Y',
  height = minHeight,
  align = 'right',
  currencyISO = 'USD',
  includeXAxis = false,
  interactive = true,
  fairValue,
}: Props) => {
  const theme = useTheme();
  const padding = 6;
  const paddingBottom = includeXAxis ? 24 : padding;
  const [width, setWidth] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debouncedSize = useDebounce(useWindowSize(), 1000);
  const { current: dateISOString } = useRef(new Date().toISOString());
  const today = dateISOString.substr(0, dateISOString.indexOf('T'));
  const { current: periodMap } = useRef(
    getTimeSeriesPeriodMap(new Date(today))
  );

  const { data = getDefault(), status } = useQuery({
    queryKey: ['price-history', companyId, periodMap[period], periodMap.NOW],
    queryFn,
    enabled: typeof companyId === 'string' && companyId !== '',
    staleTime: Infinity,
    retry: 1,
    refetchOnMount: false,
    notifyOnChangeProps: ['data', 'error'],
  });

  const { keys, entities, movement } = data;

  const xScale = scaleTime<number>({
    range: [padding, width - padding],
    domain: [
      periodMap[period],
      keys.length > 0 ? keys[keys.length - 1] : periodMap.NOW,
    ],
  });

  const [minY, maxY] = extent([data.minY, data.maxY]) as number[];
  const yScale = scaleLinear<number>({
    range: [padding, height - paddingBottom],
    domain: [maxY, minY],
  });
  yScale.clamp(true); // Enable clamp if fairValue is outside of min/max bounds

  useIsomorphicLayoutEffect(() => {
    const { current } = wrapperRef;

    if (current) {
      const nextWidth = current.getBoundingClientRect().width;
      if (nextWidth > 0 && nextWidth !== width) {
        setWidth(nextWidth);
      }
    }
  }, [debouncedSize, width]);

  const positionX = align === 'left' && keys.length > 0 ? -xScale(keys[0]) : 0;

  return (
    <Wrapper as="div" ref={wrapperRef}>
      {status === 'pending' && <Status height={height}>Loading</Status>}
      {status === 'error' && <Status height={height}>An error occurred</Status>}
      {status === 'success' && keys.length === 0 && (
        <Status height={height}>No data</Status>
      )}
      {status === 'success' && keys.length > 0 && (
        <>
          {interactive && (
            <DeprecatedNarrativeMarker
              height={height}
              keys={keys}
              entities={entities}
              periods={periodMap}
              positionX={positionX}
              xScale={xScale}
              yScale={yScale}
              currencyISO={currencyISO}
              fairValue={fairValue}
            />
          )}
          <Chart
            y="0"
            x="0"
            width={width}
            height={height}
            transform={`translate(${positionX}, 0)`}
          >
            <svg>
              <LinePath
                data={keys}
                x={(d) => xScale(d || 0) as number}
                y={(d) => yScale(entities[d] || 0) as number}
                stroke={
                  movement === Outcome.NEGATIVE
                    ? theme.color.bad
                    : theme.color.good
                }
                strokeWidth={1}
                strokeLinecap="round"
              />
              <AreaClosed
                data={keys}
                x={(d) => xScale(d || 0) as number}
                y={(d) => yScale(entities[d] || 0) as number}
                yScale={yScale} // This prop is useless but VX TS required it
                fill={movement}
                fillOpacity="0.2"
                y0={() => height + padding}
              />
            </svg>
            {includeXAxis && (
              <XAxis
                xScale={xScale}
                y={-(paddingBottom - 4)}
                height={height - (paddingBottom - 4)}
                rotateLabels={false}
              />
            )}
          </Chart>
        </>
      )}
    </Wrapper>
  );
};
